import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useEffect } from "react";

/**
 * CLAÈ rich-text editor — minimal WYSIWYG built on TipTap.
 * Preserves user-entered wording verbatim; only formatting (headings, bold,
 * italic, lists, paragraphs, line breaks) is captured. Outputs sanitised HTML.
 *
 * Props:
 *   value       (string) initial/controlled HTML content
 *   onChange    (fn)     called with fresh HTML whenever the doc changes
 *   placeholder (string) text shown when the editor is empty
 */
export default function RichTextEditor({ value, onChange, placeholder }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
    ],
    content: value || "",
    editorProps: {
      attributes: {
        class: "clae-rte prose max-w-none focus:outline-none min-h-[520px] px-6 py-6 text-[15px] leading-[1.9] text-stone-800",
        "data-testid": "admin-terms-editor",
      },
    },
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML());
    },
  });

  // Reconcile external value changes (e.g. after Restore) without clobbering
  // ongoing edits: only reset when the incoming HTML differs from what the
  // editor currently holds.
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if ((value || "") !== current) {
      editor.commands.setContent(value || "", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return null;

  const isEmpty = editor.isEmpty;

  const Btn = ({ onClick, active, label, testid }) => (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      aria-pressed={active}
      className={`mono-label text-[11px] px-3 py-1.5 border hairline transition-colors ${
        active ? "bg-stone-900 text-white border-stone-900" : "bg-white text-stone-700 hover:bg-stone-100"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="border hairline bg-white" data-testid="admin-terms-rte-shell">
      <div className="flex flex-wrap items-center gap-1.5 px-3 py-2 border-b hairline bg-stone-50 sticky top-0 z-10">
        <Btn
          testid="rte-h1"
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
          active={editor.isActive("heading", { level: 1 })}
          label="H1"
        />
        <Btn
          testid="rte-h2"
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          active={editor.isActive("heading", { level: 2 })}
          label="H2"
        />
        <Btn
          testid="rte-h3"
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
          active={editor.isActive("heading", { level: 3 })}
          label="H3"
        />
        <span className="mx-1 h-5 w-px bg-stone-300" />
        <Btn
          testid="rte-bold"
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive("bold")}
          label="Bold"
        />
        <Btn
          testid="rte-italic"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive("italic")}
          label="Italic"
        />
        <span className="mx-1 h-5 w-px bg-stone-300" />
        <Btn
          testid="rte-bullet"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive("bulletList")}
          label="• List"
        />
        <Btn
          testid="rte-ordered"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive("orderedList")}
          label="1. List"
        />
        <span className="mx-1 h-5 w-px bg-stone-300" />
        <Btn
          testid="rte-blockquote"
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          active={editor.isActive("blockquote")}
          label="Quote"
        />
        <Btn
          testid="rte-hr"
          onClick={() => editor.chain().focus().setHorizontalRule().run()}
          label="—"
        />
        <span className="mx-1 h-5 w-px bg-stone-300" />
        <Btn
          testid="rte-undo"
          onClick={() => editor.chain().focus().undo().run()}
          label="Undo"
        />
        <Btn
          testid="rte-redo"
          onClick={() => editor.chain().focus().redo().run()}
          label="Redo"
        />
        <span className="mx-1 h-5 w-px bg-stone-300" />
        <Btn
          testid="rte-clear"
          onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
          label="Clear formatting"
        />
      </div>
      <div className="relative">
        {isEmpty && placeholder && (
          <p className="pointer-events-none absolute top-6 left-6 text-stone-400 italic text-[15px] leading-[1.9]">
            {placeholder}
          </p>
        )}
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
